import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  HomeIcon,
  WalletIcon,
  UserIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  UsersIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  GiftIcon,
  EllipsisHorizontalIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import {
  HomeIcon as HomeIconSolid,
  WalletIcon as WalletIconSolid,
  UserIcon as UserIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
  ShieldCheckIcon as ShieldCheckIconSolid,
  UsersIcon as UsersIconSolid,
  RocketLaunchIcon as RocketLaunchIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  GiftIcon as GiftIconSolid,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid,
} from "@heroicons/react/24/solid";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import Modal from "../UI/Modal";
import Button from "../UI/Button";

const BottomNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const isOnAdminDashboard = location.pathname.startsWith("/admin/");
  const [isMoreModalOpen, setIsMoreModalOpen] = useState(false);

  // Get admin unread count from support slice
  const adminUnread = useSelector(
    (state: RootState) => (state as any).support?.adminUnread ?? 0,
  );

  const userUnread = useSelector(
    (state: RootState) => (state as any).support?.unreadCount ?? 0,
  );

  const userNavItems = [
    {
      path: "/dashboard",
      label: "Dashboard",
      icon: HomeIcon,
      activeIcon: HomeIconSolid,
      badge: 0,
    },
    {
      path: "/wallet",
      label: "Wallet",
      icon: WalletIcon,
      activeIcon: WalletIconSolid,
      badge: 0,
    },
    {
      path: "/trading-bots",
      label: "Bots",
      icon: RocketLaunchIcon,
      activeIcon: RocketLaunchIconSolid,
      badge: 0,
    },
    {
      path: "/referral",
      label: "Affiliate",
      icon: UserGroupIcon,
      activeIcon: UserGroupIconSolid,
      badge: 0,
    },
    // ── hidden (More) ──────────────────────────────────────────────────────
    {
      path: "/support",
      label: "Support",
      icon: ChatBubbleLeftRightIcon,
      activeIcon: ChatBubbleLeftRightIconSolid,
      badge: userUnread,
    },
    {
      path: "/airdrops",
      label: "Airdrops",
      icon: GiftIcon,
      activeIcon: GiftIconSolid,
      badge: 0,
    },
    {
      path: "/profile",
      label: "Profile",
      icon: UserIcon,
      activeIcon: UserIconSolid,
      badge: 0,
    },
  ];

  const adminNavItems = [
    {
      path: "/admin/dashboard",
      label: "Dashboard",
      icon: HomeIcon,
      activeIcon: HomeIconSolid,
      badge: 0,
    },
    {
      path: "/admin/users",
      label: "Users",
      icon: UsersIcon,
      activeIcon: UsersIconSolid,
      badge: 0,
    },
    {
      path: "/admin/support",
      label: "Support",
      icon: ChatBubbleLeftRightIcon,
      activeIcon: ChatBubbleLeftRightIconSolid,
      badge: adminUnread,
    },
    {
      path: "/admin/trading-bots",
      label: "Bots",
      icon: RocketLaunchIcon,
      activeIcon: RocketLaunchIconSolid,
      badge: 0,
    },
    {
      path: "/admin/transactions",
      label: "Transactions",
      icon: WalletIcon,
      activeIcon: WalletIconSolid,
      badge: 0,
    },
    {
      path: "/admin/kyc",
      label: "KYC",
      icon: ShieldCheckIcon,
      activeIcon: ShieldCheckIconSolid,
      badge: 0,
    },
    {
      path: "/admin/settings",
      label: "Settings",
      icon: Cog6ToothIcon,
      activeIcon: Cog6ToothIconSolid,
      badge: 0,
    },
    {
      path: "/admin/referral",
      label: "Referral",
      icon: UserGroupIcon,
      activeIcon: UserGroupIconSolid,
      badge: 0,
    },
  ];

  const getVisibleItems = (items: any[]) => items.slice(0, 4);
  const getHiddenItems = (items: any[]) => items.slice(4);

  const handleMoreClick = () => {
    setIsMoreModalOpen(true);
  };

  const handleNavItemClick = (path: string) => {
    navigate(path);
    setIsMoreModalOpen(false);
  };

  if (!user) {
    return null;
  }

  const currentNavItems = isOnAdminDashboard
    ? adminNavItems
    : (userNavItems as any[]);
  const visibleItems = getVisibleItems(currentNavItems);
  const hiddenItems = getHiddenItems(currentNavItems);
  const hasHiddenItems = hiddenItems.length > 0;

  return (
    <>
      <nav className="bottom-nav">
        <div className="bottom-nav-container">
          <div className="bottom-nav-grid">
            {visibleItems.map((item) => {
              const isActive = location.pathname === item.path;
              const IconComponent = isActive ? item.activeIcon : item.icon;

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className="bottom-nav-item"
                  style={{
                    color: isActive ? "#FF8C32" : "#9CA3AF",
                  }}
                >
                  <div className="relative">
                    <IconComponent className="w-5 h-5 md:w-6 md:h-6 mb-1" />
                    {(item as any).badge > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                        {(item as any).badge > 99 ? "99+" : (item as any).badge}
                      </span>
                    )}
                  </div>
                  <span className="truncate text-xs">{item.label}</span>
                </NavLink>
              );
            })}

            {hasHiddenItems && (
              <button
                onClick={handleMoreClick}
                className="bottom-nav-item"
                style={{ color: "#9CA3AF" }}
              >
                <div className="relative">
                  <EllipsisHorizontalIcon className="w-5 h-5 md:w-6 md:h-6 mb-1" />
                  {/* Show a dot on More if any hidden item has an unread badge */}
                  {hiddenItems.some((i: any) => i.badge > 0) && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                      {hiddenItems.reduce(
                        (sum: number, i: any) => sum + (i.badge || 0),
                        0,
                      ) > 99
                        ? "99+"
                        : hiddenItems.reduce(
                            (sum: number, i: any) => sum + (i.badge || 0),
                            0,
                          )}
                    </span>
                  )}
                </div>
                <span className="truncate text-xs">More</span>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* More Items Modal */}
      <Modal
        isOpen={isMoreModalOpen}
        onClose={() => setIsMoreModalOpen(false)}
        title="Navigation Menu"
        size="sm"
      >
        <div className="grid grid-cols-2 gap-3 p-2">
          {currentNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            const IconComponent = isActive ? item.activeIcon : item.icon;
            return (
              <div
                key={item.path}
                onClick={() => handleNavItemClick(item.path)}
                className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-gray-800 transition-colors duration-200 cursor-pointer relative"
                style={{
                  color: isActive ? "#FF8C32" : "#9CA3AF",
                }}
              >
                <div className="relative mb-2">
                  <IconComponent className="w-6 h-6" />
                  {(item as any).badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {(item as any).badge > 99 ? "99+" : (item as any).badge}
                    </span>
                  )}
                </div>
                <span className="text-sm font-medium truncate">
                  {item.label}
                </span>
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
};

export default BottomNav;
